import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse, HttpClient, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError as observableThrowError } from 'rxjs';
import { catchError, switchMap, finalize, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AppService } from '../services/app.service';
import { NGXLogger } from 'ngx-logger';
import { AuthService } from 'projects/Shared-Lib/services/auth.service';
import { ResponseErrorCode } from '../model/error-code';
import { TokenError } from '../model/tokenError';

@Injectable({
  providedIn: 'root'
})
export class RefreshTokenInterceptor implements HttpInterceptor {
  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  authService: AuthService;

  constructor(
    http: HttpClient,
    private logger: NGXLogger,
    private toastr: ToastrService,
    private router: Router,
    private appService: AppService) {
    this.authService = new AuthService(http, logger, toastr);
  }

  // intercept only process Request Header or Response
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('[RefreshTokenInterceptor][intercept]',
      { routerUrl: this.router.url, requestUrl: request.url, requestUrlWithParams: request.urlWithParams });
    this.removeTKQueryParam();
    let newRequest = this.addTokenToRequest(request, this.authService.getAuthToken());

    // 改判斷header是否有自定義 authorization
    if (request.headers.get('authorization') || request.headers.get('auth')) {
      newRequest = request;
    }

    return next.handle(newRequest).pipe(
      tap(res => {
        const { url, status, statusText, ok, type } = res as HttpResponse<any>;
        console.log('[RefreshTokenInterceptor][intercept] got response', { url, status, statusText, ok, type });
      }, (err => {
        console.error('[RefreshTokenInterceptor][intercept] error', err);
      })),
      catchError(error => {
        if (error.status == 0) {
          this.appService.Init().then(() => {
            if (window['config'].Maintenance == true) {
              this.router.navigate(['/Forbidden']);
            }
          });
        }
        if (error instanceof HttpErrorResponse && (error as HttpErrorResponse).status == 500 && error.error instanceof Blob && error.error.type === "application/json") {
          // https://github.com/angular/angular/issues/19888
          // When request of type Blob, the error is also in Blob instead of object of the json data
          const self = this;
          return new Promise<any>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: Event) => {
              try {
                const errmsg = JSON.parse((<any>e.target).result);
                const err = new HttpErrorResponse({
                  error: errmsg,
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url
                });
                self.handleOtherError(err);
                reject(new TokenError(errmsg, err));
              } catch (e) {
                reject(new TokenError('Fail to get Blob error.', e));
              }
            };
            reader.onerror = (e) => {
              reject(new TokenError('Promise read fail.', e));
            };
            reader.readAsText(error.error);
          });
        }
        if (error instanceof HttpErrorResponse) {
          const errorResponse = error as HttpErrorResponse;
          console.error('[RefreshTokenInterceptor][intercept] errorResponse', errorResponse);
          switch (errorResponse.status) {
            case 400: return this.handle400Error(error, newRequest, next);
            case 401: return this.handle401Error(error, newRequest, next);
            case 403: return this.handle403Error(error);
            default: return this.handleOtherError(error);
          }
        } else {
          this.logger.warn('TO1002', 'else error:' + error);
          return this.handleOtherError(error);
        }
      }),
      finalize(() => {
        this.authService.StartGetting = false;
      })
    ) as any;
  }

  private removeTKQueryParam() {
    const uri = this.router.url;
    const uriParts = uri.split('?');
    const uriPath = uriParts[0];
    const hasQueryParams = uriParts.length > 1;
    const preParams = hasQueryParams ? uriParts[1] : '';
    const searchParams = new URLSearchParams(preParams);
    console.log('[RefreshTokenInterceptor][removeTKQueryParam]', { uri, uriParts, uriPath, hasQueryParams, preParams });

    if (!hasQueryParams) {
      console.log(`[RefreshTokenInterceptor][removeTKQueryParam] 1.Navitgate to ${uri}`);
      return;
    }

    if (!searchParams.has('tk')) {
      console.log(`[RefreshTokenInterceptor][removeTKQueryParam] 2.Navitgate to ${uri}`);
      return;
    }

    searchParams.delete('tk');
    const postParams = searchParams.toString();
    const redirectUri = searchParams.toString() === '' ? uriPath : `${uriPath}?${postParams}`;
    console.log('[RefreshTokenInterceptor][removeTKQueryParam]', { postParams, redirectUri });
    console.log(`[RefreshTokenInterceptor][removeTKQueryParam] 3.Navitgate to ${redirectUri}`);
    this.router.navigateByUrl(redirectUri);
    return;
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `${token}`
      }
    });
  }

  private handle400Error(error, request: HttpRequest<any>, next: HttpHandler) {
    console.error('[RefreshTokenInterceptor][handle400Error]', error);
    if (error && error.status === 400 && error.error) {
      if (error.error.Message) {
        // this.toastr.error(error.error.Message, 'Redirect to error page(400)', {
        //   positionClass: 'toast-top-center',
        //   timeOut: 4000
        // });
        this.authService.removeApplicationFromLocalStroage();
        this.redirectToErrorPage(400, 'BadRequest', error);
      } else if (error.error.title && error.error.errors) {
        const errs = error.error.errors;
        let msg = '';
        Object.keys(errs).forEach(el => msg += errs[el] + ' ');
        this.toastr.error(msg, 'Sorry , there\'s something wrong(400)', {
          positionClass: 'toast-top-center',
          disableTimeOut: true
        });
        console.log('[RefreshTokenInterceptor][handle400Error]', { msg });
      }
    }
    return observableThrowError(error);
  }

  private handle401Error(error, request: HttpRequest<any>, next: HttpHandler) {
    console.error('[RefreshTokenInterceptor][handle401Error]', error);
    if (error && error.status === 401 && error.error && error.error.Code !== ResponseErrorCode.TokenExpired) {
      const errorCode = error.error.Code ? error.error.Code : 'N/A';
      const errorMessage = 'Redirect to login soon...Please wait a sec.';
      this.toastr.error(errorMessage, `Invalid token or other token errors(401/${errorCode})`, {
        positionClass: 'toast-top-center',
        timeOut: 4000
      }).onHidden.subscribe(() => {
        this.authService.removeApplicationFromLocalStroage();
        this.RedirectToLogin();
      });
      return observableThrowError(error);
    }

    if (this.isRefreshingToken) {
      return observableThrowError(error);
    }
    // Reset here so that the following requests wait until the token
    // comes back from the refreshToken call.
    this.tokenSubject.next(null);
    this.isRefreshingToken = true;

    return this.authService.RefreshToken()
      .pipe(
        switchMap((actionInfo: any) => {
          if (actionInfo && actionInfo.Success) {
            const data = actionInfo.Result;
            localStorage.setItem('access_token', data.Token);
            localStorage.setItem('user', JSON.stringify(data));
            this.tokenSubject.next(data.Token);
            const errorCode = error.error.Code ? error.error.Code : 'N/A';
            const errorMessage = 'Token is Refreshed.';
            this.toastr.warning(errorMessage, `Token expired(401/${errorCode})`, {
              positionClass: 'toast-top-center',
              timeOut: 4000
            });
            return next.handle(this.addTokenToRequest(request, data.Token));
          } else {
            this.toastr.error(actionInfo.Message, 'RefreshToken deny(401)', {
              positionClass: 'toast-top-center',
              timeOut: 4000
            }).onHidden.subscribe(() => {
              console.log('[RefreshTokenInterceptor][handle401Error]', error);
              this.authService.removeApplicationFromLocalStroage();
              this.RedirectToLogin();
            });
            return observableThrowError(actionInfo);
          }

        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
  }

  private handle403Error(error) {
    console.error('[RefreshTokenInterceptor][handle403Error]', error);
    let msg = '';
    if (error.error && error.error.Message) msg = error.error.Message;
    else if (error.error && error.error.message) msg = error.error.message;
    else if (error.message) msg = error.message;
    else msg = 'No description';
    this.appService.SetErrMsg(msg);
    // this.toastr.error(error.error.Message, 'Redirect to error page(403)', {
    //   positionClass: 'toast-top-center',
    //   timeOut: 4000
    // });
    this.authService.removeApplicationFromLocalStroage();
    this.redirectToErrorPage(403, 'Forbidden', error);
    return observableThrowError(error);
  }

  private handleOtherError(error) {
    console.error('[RefreshTokenInterceptor][handleOtherError]', error);
    let msg = '';
    if (error.error && error.error.Message) msg = error.error.Message;
    else if (error.error && error.error.message) msg = error.error.message;
    else if (error.message) msg = error.message;
    else msg = 'No description';
    // this.toastr.error(msg, 'Redirect to error page(500)', {
    //   positionClass: 'toast-top-center',
    //   timeOut: 4000
    // });
    this.authService.removeApplicationFromLocalStroage();
    this.redirectToErrorPage(500, 'InternalServer', error);
    return observableThrowError(error);
  }

  private RedirectToLogin() {
    console.log('[RefreshTokenInterceptor][RedirectToLogin]');
    this.authService.login();
  }

  private redirectToErrorPage(statusCode, statusText, error) {
    console.log('[RefreshTokenInterceptor][redirectToErrorPage]', { statusCode, statusText, error });
    const statusTextBase64 = btoa(statusText);
    const messageList = [];
    if (error && error.message) messageList.push(error.message);
    if (error && error.error && error.error.Message) messageList.push(error.error.Message);
    const messages = messageList.join('.');
    const messageBase64 = messages ? btoa(messages) : btoa('no message');
    const errorUri = `/Error?statusCode=${statusCode}&statusText=${statusTextBase64}&message=${messageBase64}`;
    this.router.navigateByUrl(errorUri);
  }
}
